import { cn } from '@nwwp/ui-shadcn'
import { ScheduleCallCTA } from '~/components/ScheduleCallCTA'
import { fmtCompact, fmtDollars, getAdvisorMessage } from './format'
import { GaugeSVG } from './GaugeSVG'
import type { CalculatorResults } from './types'
import { ONTRACK_CALENDLY_HREF } from './unlock-state'

interface ResultsSummaryProps {
  results: CalculatorResults
}

export function ResultsSummary({ results }: ResultsSummaryProps) {
  const { ratio, gap, whatYouHave, whatYouNeed, yrsInRet } = results
  const monthlyEquivalent = yrsInRet > 0 ? Math.abs(gap) / yrsInRet / 12 : 0
  const advisor = getAdvisorMessage(ratio)

  return (
    <section aria-labelledby="ontrack-results-heading" className="dark rounded-3xl bg-green-darker text-white">
      <h2 id="ontrack-results-heading" className="sr-only text-body">
        Your results
      </h2>

      <div
        aria-live="polite"
        className="grid grid-cols-1 items-center gap-6 p-5 tablet:grid-cols-2 tablet:gap-8 tablet:p-8"
      >
        <GaugeSVG ratio={ratio} className="mx-auto max-w-60" />

        <div className="flex flex-col gap-3">
          <div className="rounded-2xl bg-white/8 p-4">
            <p className="text-body-xs font-bold text-green-lighter-2 uppercase">What you&apos;ll have</p>
            <p className="text-title-sm mt-1 text-white tabular-nums">{fmtCompact(whatYouHave)}</p>
          </div>
          <div className="rounded-2xl bg-white/8 p-4">
            <p className="text-body-xs font-bold text-bone uppercase">What you&apos;ll need</p>
            <p className="text-title-sm mt-1 text-white tabular-nums">{fmtCompact(whatYouNeed)}</p>
          </div>
          <p className={cn('text-body-sm-bold tabular-nums', gap >= 0 ? 'text-green-lighter-2' : 'text-yellow-light')}>
            {gap >= 0 ? '+' : '-'}
            {fmtDollars(gap)} {gap >= 0 ? 'surplus' : 'gap'} (about {fmtDollars(monthlyEquivalent)}/mo)
          </p>
        </div>
      </div>

      <div className="border-t border-white/12 p-5 tablet:px-8">
        <p className="text-body-xs font-bold text-green-lighter-2 uppercase">{advisor.eyebrow}</p>
        <p className="text-title-xs mt-2 text-white">{advisor.headline}</p>
        <p className="mt-2 text-body-sm text-bone">
          A calculator shows you where you stand. A financial advisor gives you a roadmap.
        </p>
        <ScheduleCallCTA
          id="ontrack-results-cta"
          segmentElement="OnTrack Results Advisor CTA"
          baseUrl={ONTRACK_CALENDLY_HREF}
          variant="funnel"
          size="cta-sm"
          className="mt-5"
        >
          Book a free intro call
        </ScheduleCallCTA>
      </div>
    </section>
  )
}
